import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useSnackbar } from '../../components/SnackbarProvider';
import { UserCreateDTO, UserService, UserUpdateDTO } from '../generated';
import { userKeys } from '../queries/user';

export interface CreateUserProps {
  data: UserCreateDTO;
}

export interface UpdateUserProps {
  authId: string;
  data: UserUpdateDTO;
  showSuccessSnackbar?: boolean;
}

export interface DeleteUserProps {
  authId: string;
}

export function useCreateUserMutation() {
  const { showSnackbar } = useSnackbar();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ data }: CreateUserProps) => await UserService.userControllerCreateUser({ body: data }),
    onSuccess: async (variables) => {
      await queryClient.invalidateQueries({
        queryKey: userKeys.list({ organisationId: variables?.data?.organisationId }),
      });
      showSnackbar('administration.userManagement.inviteUserSuccess', 'success');
    },
    onError: () => {
      showSnackbar('administration.userManagement.inviteUserError', 'error');
    },
  });
}

export function useUpdateUserMutation() {
  const { showSnackbar } = useSnackbar();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ authId, data }: UpdateUserProps) =>
      UserService.userControllerUpdateUser({ path: { authId: authId }, body: data }),
    onSuccess: async (_data, variables) => {
      await queryClient.invalidateQueries({
        queryKey: userKeys.base,
      });
      if (variables.showSuccessSnackbar !== false) {
        showSnackbar('administration.userPermissions.updateUserSuccess', 'success');
      }
    },
    onError: () => {
      showSnackbar('administration.userPermissions.updateUserError', 'error');
    },
  });
}

export function useDeleteUserMutation() {
  const { showSnackbar } = useSnackbar();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ authId }: DeleteUserProps) => UserService.userControllerDeleteUser({ path: { authId: authId } }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: userKeys.base,
      });
      showSnackbar('administration.userPermissions.deleteUserSuccess', 'success');
    },
    onError: () => {
      showSnackbar('administration.userPermissions.deleteUserError', 'error');
    },
  });
}
